import { action, computed, makeObservable, observable } from 'mobx';
import { DeviceDetailEntryStore, IDeviceDetailsConstructorOptions } from './device-detail-entry-store';
import { DEVICE_JOB_EXECUTION_STATUS } from 'dto/remote-commands';
import { sleep } from 'common-utils';

export type DeviceExecutionStatuses = { [deviceName: string]: DEVICE_JOB_EXECUTION_STATUS; };

const PENDING_EXECUTION_STATUSES = [DEVICE_JOB_EXECUTION_STATUS.QUEUED, DEVICE_JOB_EXECUTION_STATUS.IN_PROGRESS];

export interface ICommandProps<T> extends ICommonCommandCode {
    commandPayload: T;
}
export interface ICommonCommandCode {
    commandCode: string;
    targetDeviceNames: string[];
    description?: string;
}

export interface ICommandCodeConstructorOptions extends IDeviceDetailsConstructorOptions {
    code: string; // Command code for payload
}

export abstract class AbstractDeviceDetailWithCommandCodeStore<T> extends DeviceDetailEntryStore<T> {
    protected readonly commandCode: string;
    private refreshRequestId = 0;

    @observable
    refreshing: boolean = false;

    constructor(name: string, options: ICommandCodeConstructorOptions) {
        super(name, options);
        this.commandCode = options.code;
        makeObservable(this);
    }

    @action
    setRefreshing(refreshing: boolean): void {
        this.refreshing = refreshing;
    }

    public async fetchLatestData(ignoreNotification?: boolean): Promise<void> {
        // Only the latest refresh request resets the flag, since an earlier one may be aborted by a newer one
        const requestId = ++this.refreshRequestId;
        this.setRefreshing(true);
        try {
            await super.fetchLatestData(ignoreNotification);
        } finally {
            if (requestId === this.refreshRequestId) this.setRefreshing(false);
        }
    }

    @computed
    protected get commandPayload(): ICommonCommandCode {
        return { commandCode: this.commandCode, targetDeviceNames: [this.uid] };
    }

    protected async triggeredCommand(): Promise<unknown> {
        const MAX_RETRY_ATTEMPTS = 2;
        const commandResult = await this._devicesStore.triggerRemoteCommand(this.accountId, this.commandPayload);
        return this.waitForExecutionCompletion(MAX_RETRY_ATTEMPTS, commandResult.executionId, [this.uid]);
    }

    protected async waitForExecutionCompletion(
        maxRetries: number,
        executionId: string,
        uids: string[],
        intervalInSeconds: number = 5,
    ): Promise<DeviceExecutionStatuses> {
        let statuses: DeviceExecutionStatuses = {};
        for (let retry = 0; retry <= maxRetries; retry++) {
            await sleep(intervalInSeconds * 1000);
            const result = await this._devicesStore.getRemoteCommandStatusByExecutionId(this.accountId, executionId, { targetDeviceNames: uids });
            statuses = {};
            for (const device of result.devices) {
                statuses[device.deviceName] = device.status;
            }
            if (!result.devices.some((device) => PENDING_EXECUTION_STATUSES.includes(device.status))) {
                break;
            }
        }
        // Extend by an additional second to ensure that the device data has been synchronized
        await sleep(1000);
        return statuses;
    }
}
